import { getMeOptions, User } from "@aura-frontend/omnibus-az-client";
import { type QueryClient } from "@tanstack/react-query";
import { backOff } from "exponential-backoff";

/**
 * User profile sync input
 * Handles syncing user data across all auth flows (email/password, OAuth, cross-product SSO)
 */
export interface SyncUserProfileInput {
  firstName?: string;
  lastName?: string;
  onSaveProfile: (input: {
    body: { givenName: string; familyName: string };
  }) => Promise<unknown>;
  queryClient: QueryClient;
}

/**
 * Loads/ensures user account exists and syncs profile data across all auth flows:
 * 1. Fetches/creates user account via backend endpoint (always safe, always needed)
 * 2. Updates profile name only if user is missing name data (avoids redundant API calls)
 *
 * Used by:
 * - Email/password signup (has firstName/lastName from form)
 * - Google/Facebook/Apple OAuth (has firstName/lastName from provider)
 * - Cross-product SSO (existing user accessing new product)
 *
 * Logic:
 * - Fetch/create user account ensures user exists in the product backend
 * - Check if user already has name
 * - Only update profile if user is missing name AND we have data to provide
 * - This avoids unnecessary API calls for existing users or cross-product scenarios
 *
 * @throws Error if user fetch/create fails
 */
export async function loadUserAccount(
  input: SyncUserProfileInput,
): Promise<void> {
  const { firstName, lastName, onSaveProfile, queryClient } = input;

  // Step 1: Call /me to create/fetch user in omnibus (always safe, always needed)
  let meResult: User | undefined;
  try {
    console.log(
      "[createUserAccount] Calling /me to ensure user exists in omnibus",
    );
    await backOff(
      async () => {
        meResult = await queryClient.fetchQuery(getMeOptions());
        if (!meResult?.userId) {
          throw new Error("Failed to fetch user profile");
        }
      },
      {
        startingDelay: 1000,
        timeMultiple: 2,
        numOfAttempts: 3,
        maxDelay: 10000,
      },
    );
    console.log("[createUserAccount] User account exists in omnibus");
  } catch (err) {
    console.error("[createUserAccount] /me failed:", err);
    throw err; // /me is critical, must succeed
  }

  // Step 2: Save profile only if user is missing name data and we have name to provide
  // This avoids redundant profile updates for:
  // - Existing users signing in again
  // - Users who already have names from other products (cross-product SSO)
  const userHasName = meResult?.givenName || meResult?.familyName;
  const hasNameToProvide = firstName || lastName;

  if (!userHasName && hasNameToProvide) {
    try {
      console.log(
        "[createUserAccount] User missing profile name, saving provided data",
      );
      await backOff(
        async () => {
          await onSaveProfile({
            body: {
              givenName: firstName || "",
              familyName: lastName || "",
            },
          });
        },
        {
          startingDelay: 1000,
          timeMultiple: 2,
          numOfAttempts: 3,
          maxDelay: 10000,
        },
      );
      console.log("[createUserAccount] Profile saved successfully");
    } catch (err) {
      console.error("[createUserAccount] Profile save failed:", err);
      // Don't fail the whole flow if profile update fails - account already created
    }
  } else if (userHasName) {
    console.log(
      "[createUserAccount] User already has profile name, skipping update",
    );
  } else {
    console.log("[createUserAccount] No profile name data provided");
    // This is fine - user can complete profile later from settings
  }
}
